import ChevronDown16 from '@carbon/icons-react/es/chevron--down/16';
import { Dropdown } from 'antd';
import React, {
  createRef,
  FC,
  ReactNode,
  RefObject,
  useCallback,
  useEffect,
  useState,
} from 'react';
import styled from 'styled-components';
import { AssigneePicker } from 'components/molecules/assignee-picker';
import { Option } from 'components/molecules/squad-picker/types';
import { Trigger } from 'components/organisms/tasks/criteria/date/components';
import { isChildNode } from 'utils/is-child-node';
import { queueMacrotask } from 'utils/macrotask';
import {
  getControlledSelectBasedEditorPopover,
  Props,
  ValueType,
} from './controlled-select-based-editor-popover';
import { withFocusableSelect } from './with-focusable-select';

export type EditorProps<
  IsMulti extends boolean,
  OptionValueType extends number | string = number,
> = Omit<Props<IsMulti, OptionValueType>, 'selectRef'> & {
  triggerContent: ReactNode;
  onClose?: () => void;
};

export const getControlledSelectBasedEditor = <
  IsMulti extends boolean,
  OptionValueType extends number | string = number,
>(
  triggerClassName: string,
  triggerDataCy: string,
): FC<EditorProps<IsMulti, OptionValueType>> => {
  const Popover =
    getControlledSelectBasedEditorPopover<IsMulti, OptionValueType>();
  const FocusablePopover = withFocusableSelect(
    Popover as unknown as any,
  ) as unknown as FC<
    Omit<Props<IsMulti, OptionValueType>, 'selectRef'> & {
      ref?: RefObject<unknown>;
    }
  >;
  const Styled = styled(FocusablePopover)`
    background: var(--white-100);
    box-shadow: var(--shadow-08);
  `;

  return ({
    title,
    onChange: onChangeActual,
    className,
    options,
    components,
    isMulti,
    value,
    onClose,
    triggerContent,
    type,
    grouped = false,
  }) => {
    const [visible, setVisible] = useState(false);
    const isChild = isChildNode([
      triggerClassName,
      'controlled-select-based-editor-popover',
      'ozone-select__multi-value__remove',
      'ozone-select__option',
    ]);
    const [focusableRef] = useState(createRef());

    const onVisibleChange = useCallback(
      (nextVisible: boolean) => {
        if (nextVisible) {
          queueMacrotask(() => (focusableRef.current as any)?.focus?.());
        } else {
          setVisible(false);
          onClose?.();
        }
      },
      [focusableRef, onClose, setVisible],
    );

    const close = useCallback(
      (event: MouseEvent) => {
        const target = event.target as HTMLElement | null;

        if (target != null && !isChild(target)) {
          onVisibleChange(false);
        }
      },
      [onVisibleChange, isChild],
    );

    useEffect(() => {
      if (!visible) {
        return () => {};
      }
      document.addEventListener('click', close);

      return () => {
        document.removeEventListener('click', close);
      };
    }, [close, visible]);

    const onChange = useCallback(
      (selected?: ValueType<IsMulti, OptionValueType>) => {
        onChangeActual(selected as any);
        if (isMulti) {
          return;
        }
        onVisibleChange(false);
      },
      [onChangeActual, isMulti, onVisibleChange],
    );

    if (triggerClassName === 'assignee-criteria') {
      return (
        <AssigneePicker
          title={title}
          hidePlaceholderSection
          hidePlaceholderCreate
          // added the below prop because two hovers are appearing, one for button wrapper, one for the trigger, and for consitency we dont need hover on button wrapper
          hideHoverBgOnButtonWrapper
          triggerComponent={
            <Trigger
              data-cy={triggerDataCy}
              className={triggerClassName}
              style={{ paddingLeft: 'var(--spacing-03)' }}
            >
              {triggerContent}
              <ChevronDown16 />
            </Trigger>
          }
          onDone={onClose}
          value={(value || []) as unknown as Option[]}
          selectProps={{
            options,
            onChange,
          }}
        />
      );
    }

    return (
      <Dropdown
        trigger={['click']}
        visible={visible}
        onVisibleChange={onVisibleChange}
        overlay={
          <Styled
            ref={focusableRef}
            className={`controlled-select-based-editor-popover ${className}`}
            title={title}
            components={components}
            isMulti={isMulti}
            options={options}
            value={value}
            onChange={onChange as any}
            type={type}
            grouped={grouped}
          />
        }
      >
        <Trigger
          data-cy={triggerDataCy}
          className={triggerClassName}
          style={{ paddingLeft: 'var(--spacing-03)' }}
          onClick={() => setVisible(!visible)}
        >
          {triggerContent}
          <ChevronDown16 />
        </Trigger>
      </Dropdown>
    );
  };
};
